import { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BkTextField from 'src/components/textfield/BkTextField';
import { addPromoCode, editPromoCode, getPromoCodes } from 'src/features/admin/utils';
import StyledLabel from 'src/pages/job-history/styled/StyledLabel';
import { AppDispatch, RootState } from 'src/store/store';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Chip, IconButton, Skeleton } from '@mui/material';
import BkTable from 'src/components/table/BkTable';
import AddPromoCode from '../modals/AddPromoCode';
import { setIsAddPromoCodeModalOpen, updatePromoCodeForm, updatePromoCodes } from 'src/features/admin/adminSlice';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { Typography } from 'src/components/new/Typography';
import { PromoCode } from 'src/features/admin/constants';

const commonStyles = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '0.5rem',
    border: '0.0625rem solid #D6D6D6',
  },
  marginBottom: '1.25rem',
};

const columns = [
  { headerName: 'Promo Code', field: 'name', cellCustomStyle: { wordBreak: 'break-all' } },
  { headerName: 'Promo Start', field: 'startAt' },
  { headerName: 'Promo End', field: 'endAt' },
  { headerName: 'Credit Expiration', field: 'printCreditEndAt' },
  {
    headerName: 'Print Credit Amount',
    field: 'printCreditAmount',
    alignSelf: 'right',
    headerCustomStyles: { textAlign: 'right ' },
  },
  { headerName: 'Status', field: 'status' },
  { headerName: 'Action', field: 'action', alignSelf: 'center' },
] as { headerName: string; field: string }[];

export const renderStatus = ({ isActivated, endAt }: { isActivated: boolean; endAt: string }) => {
  if (new Date() > new Date(endAt)) {
    return (
      <Chip
        icon={<WarningIcon sx={{ color: '#5e0a05 !important' }} />}
        sx={{ backgroundColor: '#ffe8e7', color: '#5e0a05', paddingX: '0.625rem' }}
        label="Expired"
      />
    );
  }

  if (isActivated) {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ color: '#0f3010 !important' }} />}
        sx={{ backgroundColor: '#e8fcf1', color: '#0f3010', paddingX: '0.625rem' }}
        label="Active"
      />
    );
  }
  return <Chip icon={<CancelIcon />} label="Deactivated" sx={{ paddingX: '0.625rem' }} />;
};

const PromoCodes: React.FC = () => {
  const promoCodes = useSelector((state: RootState) => state.admin.promoCodes);
  const promoCodeForm = useSelector((state: RootState) => state.admin.promoCodeForm);
  const isAddPromoCodeModalOpen = useSelector((state: RootState) => state.admin.isAddPromoCodeModalOpen);
  const isLoadingPromoCodes = useSelector((state: RootState) => state.admin.isLoadingPromoCodes);

  const dispatch: AppDispatch = useDispatch();

  const [data, setData] = useState<any>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [editPromoCodeId, setEditPromoCodeId] = useState<number | null>(null);
  const [snackBarState, setSnackbarState] = useState<{
    message: string;
    isOpened: boolean;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    message: '',
    isOpened: false,
    severity: 'error',
  });

  useEffect(() => {
    const skip = page * rowsPerPage;
    const take = rowsPerPage;
    dispatch(getPromoCodes({ searchValue: debouncedSearchValue, skip, take })).then((response) => {
      setTotalCount(response.payload.totalCount);
    });
  }, [debouncedSearchValue, page, rowsPerPage]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      setPage(0);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  useEffect(() => {
    const data = [];
    for (let code of promoCodes) {
      data.push({
        name: code.name,
        endAt: new Date(code.endAt).toLocaleDateString('en-US'),
        startAt: new Date(code.startAt).toLocaleDateString('en-US'),
        printCreditEndAt: new Date(code.printCreditEndAt).toLocaleDateString('en-US'),
        status: renderStatus({ isActivated: code.isActivated, endAt: code.endAt }),
        printCreditAmount: `$${code.printCreditAmount}`,
        action: renderAction(code.id),
      });
    }
    setData(data);
  }, [promoCodes]);

  const onEditItem = (id: number) => {
    const foundCode = promoCodes.find((code) => code.id === id) as PromoCode;
    setEditPromoCodeId(id);
    dispatch(
      updatePromoCodeForm({
        name: foundCode.name,
        startAt: new Date(foundCode.startAt).toLocaleDateString('en-US'),
        endAt: new Date(foundCode.endAt).toLocaleDateString('en-US'),
        printCreditEndAt: new Date(foundCode.printCreditEndAt).toLocaleDateString('en-US'),
        printCreditAmount: foundCode.printCreditAmount,
      }),
    );
    setIsEditModalOpen(true);
  };

  const renderAction = (id: number) => {
    return (
      <IconButton onClick={() => onEditItem(id)}>
        <EditOutlinedIcon />
      </IconButton>
    );
  };

  const onAddOrEditPromoCode = () => {
    if (isAddPromoCodeModalOpen) {
      dispatch(addPromoCode(promoCodeForm)).then((result) => {
        if (addPromoCode.fulfilled.match(result)) {
          dispatch(updatePromoCodes([result.payload.data[0], ...promoCodes]));
          dispatch(setIsAddPromoCodeModalOpen(false));
          setSnackbarState({
            isOpened: true,
            message: 'Successfully added promo code',
            severity: 'success',
          });
        }

        if (addPromoCode.rejected.match(result)) {
          setSnackbarState({
            isOpened: true,
            message: 'Something went wrong when creating promo code',
            severity: 'error',
          });
        }
      });
    } else {
      dispatch(
        editPromoCode({
          id: editPromoCodeId as number,
          endAt: new Date(promoCodeForm.endAt),
          printCreditEndAt: new Date(promoCodeForm.printCreditEndAt),
        }),
      ).then((result) => {
        if (editPromoCode.fulfilled.match(result)) {
          const updatedPromoCodes = promoCodes.map((code) =>
            code.id === editPromoCodeId
              ? { ...code, endAt: promoCodeForm.endAt, printCreditEndAt: promoCodeForm.printCreditEndAt }
              : code,
          );
          dispatch(updatePromoCodes(updatedPromoCodes));
          setSnackbarState({
            isOpened: true,
            message: 'Successfully updated promo code',
            severity: 'success',
          });
        }
        setIsEditModalOpen(false);
      });
    }
  };

  const onPromoCodeStatusChange = (data: { id: number; endAt: Date; printCreditEndAt: Date; isActivated: boolean }) => {
    dispatch(editPromoCode(data)).then((result) => {
      if (editPromoCode.fulfilled.match(result)) {
        const updatedPromoCodes = promoCodes.map((code) =>
          code.id === data.id ? { ...code, isActivated: data.isActivated } : code,
        );
        dispatch(updatePromoCodes(updatedPromoCodes));
        setIsEditModalOpen(false);
        setSnackbarState({
          isOpened: true,
          message: `Successfully ${data.isActivated ? 'reactivated' : 'deactivated'} promo code`,
          severity: 'success',
        });
      }
    });
  };

  const onModalClose = () => {
    dispatch(setIsAddPromoCodeModalOpen(false));
    setIsEditModalOpen(false);
  };

  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  return (
    <>
      <StyledLabel sx={{ fontWeight: '500' }}>Promo Code Search</StyledLabel>
      <BkTextField
        value={searchValue}
        onChange={handleSearchValueChange}
        name="search"
        error=""
        width="21.875rem"
        customStyles={commonStyles}
        hasCustomSearchIcon
      />
      {data.length > 0 ? (
        <BkTable
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          totalCount={totalCount}
          data={data}
          columns={columns}
          initialRowsPerPage={15}
        />
      ) : (
        <Typography fontSize={26} sx={{ textAlign: 'center', mt: 4 }}>
          No promo codes found.
        </Typography>
      )}
      <AddPromoCode
        mode={isAddPromoCodeModalOpen ? 'add' : 'edit'}
        open={isEditModalOpen || isAddPromoCodeModalOpen}
        onClose={onModalClose}
        onSuccess={onAddOrEditPromoCode}
        onStatusChange={onPromoCodeStatusChange}
      />
      <BkSnackBar
        open={snackBarState.isOpened}
        handleCloseSnackbar={() => setSnackbarState({ ...snackBarState, isOpened: false })}
        snackMessage={snackBarState.message}
        severity={snackBarState.severity}
      />
    </>
  );
};

export default PromoCodes;
